import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { authService, isMfaChallenge, MfaChallenge, SignedIn } from '../api/auth';
import MfaChallengeForm from '../components/MfaChallengeForm';
import { login } from '../store/authSlice';
import { AppDispatch } from '../../../store/store';
import { colors } from '../../../theme';
import { returnPathAfterSignIn } from '../../../commodity/config/routes';

interface FormState {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const location = useLocation();
  const handoff =
    (location.state as {
      verified?: boolean;
      passwordReset?: boolean;
      email?: string;
      from?: unknown;
      signOutNotice?: string;
    } | null) ?? null;

  const [form, setForm] = useState<FormState>({ email: handoff?.email ?? '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  // Confirmation carried over from the verification / password-reset screens.
  const notice = handoff?.verified
    ? 'Tu correo quedó verificado. Ya puedes iniciar sesión.'
    : handoff?.passwordReset
    ? 'Tu contraseña se actualizó. Inicia sesión con la nueva.'
    : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setApiError(null);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (!form.password) {
      newErrors.password = 'Password is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const finishSignIn = (result: SignedIn) => {
    dispatch(login({ user: result.user, accessToken: result.accessToken }));
    // Resume what sign-in interrupted; otherwise the console for this role,
    // not the public catalog.
    navigate(returnPathAfterSignIn(handoff?.from, result.user.role), { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    try {
      const result = await authService.login({
        email: form.email,
        password: form.password,
      });
      // Operator MFA (CTO decision 12 Oct 2026): an ADMIN/MODERATOR — or any
      // account with an authenticator — is asked for a code before tokens.
      if (isMfaChallenge(result)) {
        setChallenge(result);
        return;
      }
      finishSignIn(result);
    } catch (err: any) {
      const serverMsg: string = err?.response?.data?.message || err?.message || '';
      // A network/connectivity failure (or a CORS-less 5xx) surfaces as a raw
      // "Failed to fetch" / "Network Error" — never show that to the user.
      // An account that exists but was never confirmed is not an error state —
      // it is an unfinished signup. Send them to finish it.
      // The adapter says "User email not verified"; this matched only "not
      // confirmed", so the redirect never fired and the parent read the raw
      // English error instead of being sent to finish their signup.
      if (/not\s*(confirmed|verified)|UserNotConfirmed/i.test(serverMsg)) {
        navigate('/verify-email', { state: { email: form.email.trim() } });
        return;
      }
      const isNetworkError =
        !serverMsg || /failed to fetch|network\s*error|load failed/i.test(serverMsg);
      const message = isNetworkError
        ? "We couldn't reach the server right now. Please check your connection and try again in a moment."
        : serverMsg;
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f7fb',
        py: 6,
        px: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: '#ffffff',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ color: colors.primary.main, letterSpacing: -0.5 }}
            >
              IQMaxx
            </Typography>
          </Box>

          {challenge ? (
            <MfaChallengeForm
              challenge={challenge}
              onSignedIn={finishSignIn}
              onRestart={() => {
                setChallenge(null);
                setForm((prev) => ({ ...prev, password: '' }));
              }}
            />
          ) : (
            <>
              {/* Heading */}
              <Typography
                variant="h5"
                fontWeight={700}
                align="center"
                sx={{ mb: 0.75, color: 'text.primary' }}
              >
                Welcome back!
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                Sign in to continue learning.
              </Typography>

              {/* API Error */}
              {notice && !apiError && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  {notice}
                </Alert>
              )}

              {/* PRT-17-FR-01 — signed out here, but the server could not confirm the
              session is dead elsewhere. Settings used to hand this over and
              nothing rendered it. A warning, not a success: it asks for action. */}
              {handoff?.signOutNotice && !apiError && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  {handoff.signOutNotice}
                </Alert>
              )}

              {apiError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {apiError}
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                {/* Email */}
                <TextField
                  fullWidth
                  label="Email address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  autoComplete="email"
                  sx={{ mb: 2 }}
                />

                {/* Password */}
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />

                {/* Forgot password */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    underline="hover"
                    sx={{ color: colors.primary.main, fontWeight: 500 }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                {/* Submit */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderRadius: 2,
                    backgroundColor: colors.primary.main,
                    '&:hover': { backgroundColor: colors.primary.dark },
                    mb: 2,
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                </Button>

                {/* Sign up link */}
                <Typography variant="body2" align="center" color="text.secondary">
                  New here?{' '}
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/register')}
                    underline="hover"
                    sx={{ color: colors.primary.main, fontWeight: 600 }}
                  >
                    Create an account
                  </Link>
                </Typography>
              </Box>

              {/* Back link */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  startIcon={<ArrowBack fontSize="small" />}
                  onClick={() => navigate('/auth')}
                  size="small"
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  Back to all sign-in options
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
